import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap/modal';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  PasAccordionBodyDirective,
  PasAccordionComponent,
  PasButtonComponent,
  PasCheckboxComponent,
  PasConfirmationService,
  PasInputFieldComponent,
  PasInputNumberComponent,
  PasLayoutSidebarHolderComponent,
  PasSelectComponent,
  PasSidebarHandleService,
  PasSpinnerComponent,
  PasSystemProcessComponent,
} from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { FirstDataRenderedEvent, GridOptions, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { cloneDeep, isEqual } from 'lodash-es';

import { DecimalFormatConverter, UserLanguage } from '../../../core/decimal-format-converter';
import { DateFormatPipe } from '../../../pipes/date-format.pipe';
import {
  showAdditionalCostsDeleteFailed,
  showAdditionalCostsDeleteSuccess,
  translateAuditReportState,
} from '../../../services/after-sales/after-sales-functions';
import { AfterSalesStore } from '../../../services/after-sales/after-sales-store.service';
import { AfterSalesService } from '../../../services/after-sales/after-sales.service';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { RecourseBookingStore } from '../../../services/booking/recourse-booking-store';
import { BookingRoutingService } from '../../../services/details-view-service/booking-routing.service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { SupplierModel } from '../../../services/supplier-service/model/supplier.model';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { AuditReportRecourseStateEnum } from '../../../shared/enums/audit-report-recourse-state-enum';
import { AuditReportStateEnum } from '../../../shared/enums/audit-report-state-enum';
import { BookingStateEnum } from '../../../shared/enums/booking-state-enum';
import { RecourseTypeIdEnum } from '../../../shared/enums/recourse-type-id-enum';
import { GenericModalContentComponent } from '../../../shared/generic-modal-content/generic-modal-content.component';
import { getAdditionalCostsCategoryDropdownList } from '../../../shared/model-serializer/mapper-functions';
import { RecourseBookingModel } from '../../recourse-booking/model/recourse-booking-model';
import { AdditionalCostModel } from '../model/additional-cost-model';
import { AfterSalesModel } from '../model/after-sales-model';
import { AfterSalesButtonCellRenderer } from '../renderer/after-sales-button-cell-renderer.component';

@Component({
  selector: 'app-after-sales-details',
  templateUrl: './after-sales-details.component.html',
  styleUrls: ['./after-sales-details.component.scss'],
  imports: [
    ReactiveFormsModule,
    PasSystemProcessComponent,
    TranslatePipe,
    PasCheckboxComponent,
    PasAccordionComponent,
    PasAccordionBodyDirective,
    PasButtonComponent,
    AgGridAngular,
    NgClass,
    PasLayoutSidebarHolderComponent,
    GenericModalContentComponent,
    PasSelectComponent,
    PasInputNumberComponent,
    PasSpinnerComponent,
    PasInputFieldComponent,
  ],
})
export class AfterSalesDetailsComponent implements OnInit, AfterViewInit {
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly dateFormatPipe = inject(DateFormatPipe);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(NgbModal);
  private readonly afterSalesStore = inject(AfterSalesStore);
  private readonly afterSalesService = inject(AfterSalesService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly agGridService = inject(AgGridService);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly bookingRoutingService = inject(BookingRoutingService);
  private readonly cdRef = inject(ChangeDetectorRef);

  @Output() public signal = new EventEmitter();

  @ViewChild('processComponent')
  private readonly processComponent: PasSystemProcessComponent;

  @ViewChild('AdditionalCostsDialogTemplate')
  public additionalCostsDialogTemplate: ElementRef;
  public additionalCostsDialog: NgbModalRef;
  public additionalCostDialogForm: UntypedFormGroup;
  public additionalCostsCategoryDropdownList: any[] = [];
  public selectedCategory: any;
  public selectedAdditionalCostsFromTable: AdditionalCostModel = null;
  public acceptanceAllowed = false;

  public afterSalesDetailForm: UntypedFormGroup;
  public additionalCostFormArray: UntypedFormArray;
  private recourseCostRate: number;
  public formattedRecourseCostRate = '';
  public additionalCostsSum = 0;
  public formattedAdditionalCostsSum = '';
  public formattedTotalCosts = '';
  public data: AfterSalesModel;
  private isAuditReportValid = true;
  public sizeOfAditionalCosts = 0;
  public validationMessages: { error: string; message: string }[];
  public formValueChanged = false;
  public initialFormValue: any;
  public isInCorrespondingState = false;
  public isReportStateInStatusClosed = false;

  public decimalSeparator: ',' | '.' = '.';
  public userLanguage: '' | 'DE';
  public spinnerActive = false;

  public bookingTableColumnDefs: any;
  public bookingGridOptions: GridOptions;
  public frameworkComponents: any;
  public defaultColDef: any;
  public gridApi: any;

  private tmpParam: any;

  public constructor() {
    this.frameworkComponents = {
      buttonDetailsRenderer: AfterSalesButtonCellRenderer,
    };
  }

  public ngOnInit(): void {
    this.initializeView();
  }

  public initializeView(): void {
    this.data = this.afterSalesStore.selectedAfterSalesDetail;
    this.checkIfInMeantimeManipulated(this.data);
    this.additionalCostsCategoryDropdownList = this.afterSalesStore.additionalCostsCategoryListSelectMenu;
    this.recourseCostRate = this.data.recourseCostRate;

    this.sidebarHandleService.enableSidebarForView();
    this.decimalSeparator = this.userService.userLanguage.startsWith('en') ? '.' : ',';
    this.userLanguage = this.userService.userLanguage.startsWith('en') ? '' : 'DE';

    this.initValidationMessages();
    this.initFormFields();
    this.populateFormValues(this.data);
    this.isReportStateCancelledAndRecourseStateClosed(this.data);
    this.isReportStateClosed(this.data);

    this.configureBookingTableColumnDefs();

    this.initialFormValue = cloneDeep(this.additionalCostDialogForm.value);
    this.onChanges();
  }

  private checkIfInMeantimeManipulated(data: AfterSalesModel): void {
    this.isAuditReportValid = true;
    if (this.afterSalesStore.selectedAuditReportId) {
      this.translateService
        .get([
          'AUTHENTICITY_AUDIT_REPORT_FAILED_TITLE',
          'AUTHENTICITY_ERROR_COST_RATE_FAILED_TITLE',
          'AUTHENTICITY_AUDIT_REPORT_ADDITIONAL_COSTS_FAILED_TITLE',
        ])
        .subscribe((translate: { [key: string]: any }) => {
          let title: string;
          let content: string;
          if (!data.signatureValid) {
            this.isAuditReportValid = false;
            title = translate.AUTHENTICITY_AUDIT_REPORT_FAILED_TITLE;
            content = this.translateService.instant('AUTHENTICITY_AUDIT_REPORT_FAILED', {
              0: this.afterSalesStore.selectedAuditReportId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
          if (!data.signatureErrorCodeRateValid) {
            this.isAuditReportValid = false;
            title = translate.AUTHENTICITY_ERROR_COST_RATE_FAILED_TITLE;
            content = this.translateService.instant('AUTHENTICITY_ERROR_COST_RATE_FAILED_LONG', {
              0: this.afterSalesStore.selectedAuditReportId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
          if (!data.signatureAdditionalCostsValid) {
            this.isAuditReportValid = false;
            title = translate.AUTHENTICITY_AUDIT_REPORT_ADDITIONAL_COSTS_FAILED_TITLE;
            content = this.translateService.instant('AUTHENTICITY_AUDIT_REPORT_ADDITIONAL_COSTS_FAILED', {
              0: this.afterSalesStore.selectedAuditReportId,
            });
            this.notificationServiceWrapper.addErrorNotification(title, content);
          }
        });
    }
  }

  // BOOKINGS
  private configureBookingTableColumnDefs(): void {
    this.bookingGridOptions = {
      rowHeight: 35,
      suppressCellFocus: true,
      overlayNoRowsTemplate: `<span>${this.translateService.instant('AG_GRID_NO_BOOKINGS')}</span>`,
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };
    this.bookingGridOptions.pagination = false;
    this.bookingGridOptions.suppressCellFocus = true;
    this.bookingGridOptions.context = {
      credit: 'CREDIT',
      cancelledState: BookingStateEnum.CANCELLED,
    };

    this.bookingGridOptions.rowClass = 'white-row';
    this.bookingGridOptions.rowClassRules = {
      'gray-characters': 'data.bookingStateId === ctx.cancelledState',
      'red-characters': 'data.bookingType === ctx.credit && data.bookingStateId !== ctx.cancelledState',
    };

    this.defaultColDef = {
      resizable: true,
      sortable: false,
      suppressHeaderMenuButton: true,
    };

    this.bookingTableColumnDefs = [
      {
        headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_ID'),
        field: 'pkBookingId',
        sort: 'desc',
      },
      {
        headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_AMOUNT'),
        field: 'amount',
        type: 'rightAligned',
        valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
      },
      {
        headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_CREDITOR_NO'),
        field: 'creditorNo',
      },
      { headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_CREDITOR'), field: 'creditor' },
      {
        headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_DUE_DATE'),
        field: 'dueDate',
        valueFormatter: this.agGridFormatterService.dateFormatter,
      },
      {
        headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_BOOKING_TYPE'),
        field: 'bookingType',
        valueGetter: this.agGridService.translateBookingType(this.translateService),
      },
      {
        headerName: this.translateService.instant('AFTER_SALES_DETAIL_TABLE_BOOKING_STATE'),
        valueGetter: this.agGridService.getBookingStateName(this.translateService),
      },
      {
        headerName: '',
        headerClass: ['ag-header-last-cell'],
        minWidth: 50,
        maxWidth: 50,
        field: 'value',
        cellRenderer: 'buttonDetailsRenderer',
        filter: false,
        cellRendererParams: {
          onClick: this.onBtnBookingDetails.bind(this),
        },
        menuTabs: [],
        unSortIcon: false,
      },
    ];
  }

  public onBtnBookingDetails(event: any): void {
    this.bookingRoutingService.openBookingDetailsView(event.rowData.pkBookingId);
  }

  private onChanges(): void {
    this.additionalCostDialogForm.valueChanges.subscribe(newFormValue => {
      this.formValueChanged = this.areFormFieldsChanged(this.initialFormValue, newFormValue);
    });
  }

  private areFormFieldsChanged(initialFormValue, newFormValue): boolean {
    let formChanged = false;
    Object.keys(initialFormValue).forEach((key: string) => {
      const initialFieldValue = initialFormValue[key];
      const newFieldValue = newFormValue[key];
      if (key === 'additionalCost') {
        if (!this.areNumericValuesEqual(initialFieldValue, newFieldValue)) {
          formChanged = true;
        }
      } else {
        if (!isEqual(initialFieldValue, newFieldValue) && !this.stringsNullOrEmpty(initialFieldValue, newFieldValue)) {
          formChanged = true;
        }
      }
    });
    return formChanged;
  }

  private areNumericValuesEqual(initValue, newValue): boolean {
    return Number(initValue) === Number(newValue);
  }

  private stringsNullOrEmpty(initValue, newValue): boolean {
    return this.isStringNullOrEmpty(initValue) && this.isStringNullOrEmpty(newValue);
  }

  private isStringNullOrEmpty(inputString): boolean {
    return !inputString || inputString.length === 0;
  }

  private initValidationMessages(): void {
    this.validationMessages = [
      {
        error: 'required',
        message: this.translateService.instant('VALIDATION_INPUT_REQUIRED'),
      },
    ];
  }

  private initFormFields(): void {
    this.afterSalesDetailForm = new UntypedFormGroup({
      reportId: new UntypedFormControl({ value: '', disabled: true }),
      supplierNo: new UntypedFormControl({ value: '', disabled: true }),
      supplierName: new UntypedFormControl({ value: '', disabled: true }),
      errorCode: new UntypedFormControl({ value: '', disabled: true }),
      errorDescription: new UntypedFormControl({ value: '', disabled: true }),
      errorClass: new UntypedFormControl({ value: '', disabled: true }),
      reportState: new UntypedFormControl({ value: '', disabled: true }),
      recourseRelevant: new UntypedFormControl({ value: '', disabled: true }),
      reportCreationDate: new UntypedFormControl({ value: '', disabled: true }),
      totalAmountForBookings: new UntypedFormControl({ value: '', disabled: true }),
      additionalCostItems: this.createAdditionalCostFormArray(this.data.additionalCostItems),
    });

    this.additionalCostDialogForm = new UntypedFormGroup({
      category: new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
      comment: new UntypedFormControl({ value: '', disabled: false }),
      additionalCost: new UntypedFormControl({ value: '', disabled: false }, { validators: [Validators.required] }),
    });
  }

  private populateFormValues(data: AfterSalesModel): void {
    if (data.additionalCostItems != null) {
      this.sizeOfAditionalCosts = data.additionalCostItems.length;
    }

    this.afterSalesDetailForm.patchValue({
      reportId: data.reportId,
      supplierNo: data.supplierNo,
      supplierName: data.supplierName,
      errorCode: data.errorCode,
      errorDescription: data.errorDescription,
      errorClass: data.errorClass,
      reportState: translateAuditReportState(data.reportState, this.translateService),
      recourseRelevant: data.recourseRelevant,
      reportCreationDate: this.dateFormatPipe.transformDateToGermanFormat(data.reportCreationDate),
      // BOOKING
      totalAmountForBookings:
        this.data.totalAmountForBookings == null
          ? 0
          : new DecimalFormatConverter(
              this.userService.userLanguage as UserLanguage,
            ).numberCurrencyFormatterByLocalLang(this.data.totalAmountForBookings, 'EUR'),
    });

    this.populateAdditionalCostFormArrayValues(data.additionalCostItems);

    this.formattedRecourseCostRate = new DecimalFormatConverter(
      this.userService.userLanguage as UserLanguage,
    ).numberFormatterWithSeparators(this.recourseCostRate, 2, 2);
  }

  private populateAdditionalCostForm(itemIndex?: number): void {
    const selectedRow: any = this.additionalCostFormArray.value[itemIndex];

    const selectedAdditionalCostRowFromTable = new AdditionalCostModel();
    selectedAdditionalCostRowFromTable.additionalCostsId = selectedRow.additionalCostsId;
    selectedAdditionalCostRowFromTable.fkAuditReportId = selectedRow.fkAuditReportId;
    selectedAdditionalCostRowFromTable.comment = selectedRow.comment;
    selectedAdditionalCostRowFromTable.additionalCost = new DecimalFormatConverter(
      this.userService.userLanguage as UserLanguage,
    ).reverseNumberFormatterWithSeparators(selectedRow.additionalCost);

    selectedAdditionalCostRowFromTable.categoryDTO = {
      categoryId: selectedRow.categoryId,
    };
    this.selectedAdditionalCostsFromTable = selectedAdditionalCostRowFromTable;

    this.additionalCostDialogForm.setValue({
      category: selectedRow.categoryId,
      comment: selectedRow.comment,
      additionalCost: new DecimalFormatConverter(
        this.userService.userLanguage as UserLanguage,
      ).reverseNumberFormatterWithSeparators(selectedRow.additionalCost),
    });

    this.selectedCategory = getAdditionalCostsCategoryDropdownList(
      selectedRow.categoryId,
      selectedRow.categoryTranslated,
      this.translateService,
    );
  }

  private resetAdditionalCostForm(): void {
    this.additionalCostDialogForm.setValue({
      category: '',
      comment: '',
      additionalCost: '',
    });

    this.selectedAdditionalCostsFromTable = null;
    this.selectedCategory = '';
  }

  public ngAfterViewInit(): void {
    this.sidebarHandleService.enableSidebarForView();
    // prevent angular error “Expression has changed after it was checked” in dev mode
    this.cdRef.detectChanges();
  }

  public isSignatureOk(): boolean {
    return this.isAuditReportValid;
  }

  public onBtnCancel(): void {
    this.signal.emit();
  }

  public userHasEditRight(): boolean {
    return this.userService.getRights().includes(UserRights.AFTERSALES_EDIT);
  }

  private createAdditionalCostFormArray(additionalCostItems?: AdditionalCostModel[]): UntypedFormArray {
    // FormArray has to be seperated and inside FormGroup to get access to them in the template
    this.additionalCostFormArray = new UntypedFormArray([]);

    if (additionalCostItems) {
      additionalCostItems.forEach(() => this.createFormItem());
    } else if (additionalCostItems === null) {
      this.formatAndCalculateInitValues(0);
    }
    this.sizeOfAditionalCosts = this.additionalCostFormArray.length;
    return this.additionalCostFormArray;
  }

  private createFormItem(item?: AdditionalCostModel): void {
    const additionalCostFormTable = new UntypedFormGroup({
      additionalCostsId: new UntypedFormControl({ value: '', disabled: true }),
      fkAuditReportId: new UntypedFormControl({ value: '', disabled: true }),
      categoryId: new UntypedFormControl({ value: '', disabled: true }),
      categoryTranslated: new UntypedFormControl({ value: '', disabled: true }),
      comment: new UntypedFormControl({ value: '', disabled: true }),
      additionalCost: new UntypedFormControl({ value: '', disabled: true }),
    });

    this.additionalCostFormArray.push(additionalCostFormTable);
    if (item != undefined) {
      this.populateFormNewItem(item);
    }
  }

  private populateFormNewItem(item: AdditionalCostModel): void {
    this.sizeOfAditionalCosts = this.additionalCostFormArray.length;
    const control = this.additionalCostFormArray.controls[this.sizeOfAditionalCosts - 1];

    this.populateFormItem(item, control);
  }

  private populateFormItem(item: AdditionalCostModel, control: AbstractControl): void {
    control.get('additionalCostsId').setValue(item.additionalCostsId);
    control.get('fkAuditReportId').setValue(item.fkAuditReportId);
    control.get('categoryId').setValue(item.categoryDTO.categoryId);
    control.get('categoryTranslated').setValue(this.translateService.instant(item.categoryDTO.categoryName));
    control.get('comment').setValue(item.comment);
    control
      .get('additionalCost')
      .setValue(
        new DecimalFormatConverter(this.userService.userLanguage as UserLanguage).numberFormatterWithSeparators(
          item.additionalCost,
          2,
          2,
        ),
      );

    this.formatAndCalculateInitValues(item.additionalCost);
  }

  private populateAdditionalCostFormArrayValues(additionalCostItems?: AdditionalCostModel[]): void {
    if (additionalCostItems != undefined) {
      this.additionalCostFormArray.controls.forEach((control, index) => {
        const item = additionalCostItems[index];
        this.populateFormItem(item, control);
      });
    }
  }

  private updateFormItem(updatedDTO: AdditionalCostModel): void {
    const itemIndex = this.additionalCostFormArray.value.findIndex(
      (item: AdditionalCostModel) => item.additionalCostsId === updatedDTO.additionalCostsId,
    );
    const controlForUpdate: any = this.additionalCostFormArray.controls[itemIndex];
    this.populateFormItem(updatedDTO, controlForUpdate);

    this.additionalCostFormArray.updateValueAndValidity();
  }

  private removeFormItem(itemIndex: number, additionalCost: string): void {
    this.additionalCostFormArray.removeAt(itemIndex);
    this.sizeOfAditionalCosts = this.additionalCostFormArray.length;

    this.formatAndRecalculateValues(
      new DecimalFormatConverter(this.userService.userLanguage as UserLanguage).reverseNumberFormatterWithSeparators(
        additionalCost,
      ),
    );
  }

  public onNewAdditionalCost(): void {
    this.openAdditionalCostsDialog();
  }

  public onEditAdditionalCost(itemIndex: number): void {
    this.populateAdditionalCostForm(itemIndex);
    this.openAdditionalCostsDialog();
  }

  public openAdditionalCostsDialog(): void {
    this.additionalCostsDialog = this.modalService.open(this.additionalCostsDialogTemplate, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
    });
  }

  public onCloseAdditionalCostsDialog(): void {
    this.resetAdditionalCostForm();
    this.additionalCostsDialog.close();
  }

  public onAcceptAdditionalCostsDialog(): void {
    this.spinnerActive = true;
    this.disableAdditionalCostForm(true);

    this.afterSalesService.saveAdditionalCost(this.createOrUpdateAdditionalCost()).subscribe({
      next: savedAdditionalCost => {
        const title: string = this.translateService.instant('SAVE_ADDITIONAL_COST_SUCCESS_TITLE');
        const content: string = this.translateService.instant('SAVE_ADDITIONAL_COST_SUCCESS_MESSAGE', {
          0: this.afterSalesStore.selectedAuditReportId,
        });
        this.notificationServiceWrapper.addSuccessNotification(title, content);
        if (this.selectedAdditionalCostsFromTable) {
          this.updateFormItem(savedAdditionalCost);
        } else {
          this.createFormItem(savedAdditionalCost);
        }
      },
      error: () => {
        const title: string = this.translateService.instant('SAVE_ADDITIONAL_COST_ERROR_TITLE');
        const content: string = this.translateService.instant('SAVE_ADDITIONAL_COST_ERROR_MESSAGE', {
          0: this.afterSalesStore.selectedAuditReportId,
        });
        this.notificationServiceWrapper.addErrorNotification(title, content);
        this.disableAdditionalCostForm(false);
        this.spinnerActive = false;
        this.onCloseAdditionalCostsDialog();
      },
      complete: () => {
        this.disableAdditionalCostForm(false);
        this.spinnerActive = false;
        this.onCloseAdditionalCostsDialog();
      },
    });
  }

  private createOrUpdateAdditionalCost(): AdditionalCostModel {
    const additionalCostDialogData = this.additionalCostDialogForm.value;

    const additionalCostModel = new AdditionalCostModel();
    // NEW : UPDATE of additional cost
    additionalCostModel.additionalCostsId =
      this.selectedAdditionalCostsFromTable === null ? null : this.selectedAdditionalCostsFromTable.additionalCostsId;
    additionalCostModel.fkAuditReportId = this.afterSalesStore.selectedAuditReportId;
    additionalCostModel.comment = additionalCostDialogData.comment;
    additionalCostModel.additionalCost = additionalCostDialogData.additionalCost;

    additionalCostModel.categoryDTO = {
      categoryId: additionalCostDialogData.category.key,
    };

    return additionalCostModel;
  }

  public onDeleteAdditionalCost(itemIndex: number): void {
    const selectedRow: any = this.additionalCostFormArray.value[itemIndex];
    const additionalCostId = selectedRow.additionalCostsId;
    const categoryTranslated = selectedRow.categoryTranslated;
    const comment = selectedRow.comment;
    const additionalCost = selectedRow.additionalCost;
    const selectedAuditReportId: string = this.afterSalesStore.selectedAuditReportId;

    this.translateService
      .get(['DELETE_ADDITIONAL_COST_TITLE', 'DELETE_ADDITIONAL_COST_MESSAGE', 'OK', 'CANCEL'])
      .subscribe((translate: { [key: string]: any }) => {
        const title: string = translate.DELETE_ADDITIONAL_COST_TITLE;
        const message: string = this.translateService.instant('DELETE_ADDITIONAL_COST_MESSAGE', {
          0: categoryTranslated,
          1: comment,
          2: additionalCost,
        });
        const ok: string = translate.OK;
        const dismiss: string = translate.CANCEL;

        this.pasConfirmationService.showConfirmation(title, message, ok, dismiss, true).then(
          () => {
            this.processComponent.startProcess();

            this.afterSalesService.deleteAdditionalCost(additionalCostId).subscribe({
              next: () => {
                this.processComponent.stopProcess();
                showAdditionalCostsDeleteSuccess(
                  this.notificationServiceWrapper,
                  this.translateService,
                  additionalCostId,
                  selectedAuditReportId,
                );

                this.removeFormItem(itemIndex, additionalCost);
              },
              error: () => {
                this.processComponent.stopProcess();
                showAdditionalCostsDeleteFailed(
                  this.notificationServiceWrapper,
                  this.translateService,
                  additionalCostId,
                  selectedAuditReportId,
                );
              },
            });
          },
          () => {},
        );
      });
  }

  private formatAndCalculateInitValues(additionalCost: number): void {
    if (this.selectedAdditionalCostsFromTable) {
      this.additionalCostsSum = this.additionalCostsSum - this.selectedAdditionalCostsFromTable.additionalCost;
    }
    this.additionalCostsSum = this.additionalCostsSum + additionalCost;
    this.formatValuesSumAndTotal();
  }

  private formatAndRecalculateValues(additionalCost: number): void {
    this.additionalCostsSum = this.additionalCostsSum - additionalCost;
    this.formatValuesSumAndTotal();
  }

  private formatValuesSumAndTotal(): void {
    this.formattedAdditionalCostsSum = new DecimalFormatConverter(
      this.userService.userLanguage as UserLanguage,
    ).numberFormatterWithSeparators(this.additionalCostsSum, 2, 2);
    this.formattedTotalCosts = new DecimalFormatConverter(
      this.userService.userLanguage as UserLanguage,
    ).numberFormatterWithSeparators(this.recourseCostRate + this.additionalCostsSum, 2, 2);
  }

  public isAcceptanceAllowed(): boolean {
    return (
      this.additionalCostDialogForm &&
      this.additionalCostDialogForm.dirty &&
      this.additionalCostDialogForm.valid &&
      this.formValueChanged
    );
  }

  public disableAdditionalCostForm(disableFlag: boolean): any {
    return disableFlag === true ? this.additionalCostDialogForm.disable() : this.additionalCostDialogForm.enable();
  }

  private isReportStateCancelledAndRecourseStateClosed(selectedAfterSalesDetails: AfterSalesModel): void {
    this.isInCorrespondingState =
      selectedAfterSalesDetails.reportState === AuditReportStateEnum.CANCELLED ||
      selectedAfterSalesDetails.reportState === AuditReportStateEnum.CLOSED ||
      selectedAfterSalesDetails.recourseState === AuditReportRecourseStateEnum.BOOKED;
  }

  private isReportStateClosed(selectedAfterSalesDetails: AfterSalesModel): void {
    this.isReportStateInStatusClosed = selectedAfterSalesDetails.reportState === AuditReportStateEnum.CLOSED;
  }

  public additionalCostsButtonsVisible(): boolean {
    return (
      this.isSignatureOk() && this.userHasEditRight() && !this.isInCorrespondingState && this.data.bookings.length === 0
    );
  }

  public onFirstDataRendered(event: FirstDataRenderedEvent<any>): void {
    event.api.sizeColumnsToFit();
  }

  public onBookingGridReady(event: GridReadyEvent): void {
    this.tmpParam = event;
    this.gridApi = event.api;
    this.gridApi.sizeColumnsToFit();
    if (this.data.bookings) {
      event.api.setGridOption('rowData', this.data.bookings);
    }
  }

  public onGridSizeChanged(params: GridSizeChangedEvent): void {
    const columnsToShow = [];
    const columnsToHide = [];
    params.api.setColumnsVisible(columnsToShow, true);
    params.api.setColumnsVisible(columnsToHide, false);
    params.api.sizeColumnsToFit();
  }

  public refreshBookingsTable(): void {
    this.onBookingGridReady(this.tmpParam);
  }

  public isCreateManualBookingVisible(): boolean {
    return this.userService.getRights().includes(UserRights.AFTERSALES_BOOKING) && this.isReportStateInStatusClosed;
  }

  public onBtnCreateManualBooking(): void {
    const supplierData: SupplierModel = new SupplierModel();
    supplierData.supplier = this.data.supplierNo;
    const newBookingModel: RecourseBookingModel = new RecourseBookingModel();
    newBookingModel.supplierData = supplierData;
    newBookingModel.recourseId = this.data.reportId;
    newBookingModel.recourseTypeId = RecourseTypeIdEnum.AFTERSALES;

    this.recourseBookingStore.setRecourseId(this.data.reportId);
    this.bookingRoutingService.createNewBooking(newBookingModel);
  }
}
